import PropTypes from 'prop-types'
import { useEffect, useState, useRef, useContext } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { useTranslation } from 'next-i18next'

// Context
import { FilterContext } from '../../context/FilterContext'
import { UserContext } from '../../context/UserContext'

// Internal component
import FilterDropdown from '../FilterDropdown/FilterDropdown'
import FilterCheckbox from '../FilterCheckbox/FilterCheckbox'
import Svg from '../Svg/Svg'

// Utils
import { getLocalStorage, setLocalStorage } from '../../utils/store/localstorage'

const selectedFilterStorageKey = 'selectedFilterStorage'

const OverviewFilter = (props) => {
  const { data, type } = props

  // Context
  const filterContext = useContext(FilterContext)
  const userContext = useContext(UserContext)

  const getDefaultFilterValues = (data) => {
    const defaultSelectValues = {}
    if (!data) return defaultSelectValues
    data.map((filter) => (defaultSelectValues[filter.name] = ''))
    return defaultSelectValues
  }

  // Translations
  const { t } = useTranslation(
    userContext?.userSession?.sportType?.slug ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_NAMESPACE ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_SPORT
  )

  const [clearFilters, triggerClearFilters] = useState(false)
  const [toggleState, setToggleState] = useState(false)
  const [defaultFormValues] = useState(getDefaultFilterValues)

  // State classes
  const openClass = 'is-open'
  const formRef = useRef()
  const methods = useForm({ defaultValues: defaultFormValues })

  useEffect(() => {
    if (clearFilters) triggerClearFilters(false)
  }, [clearFilters])

  useEffect(() => {
    if (filterContext.resetFilters) {
      resetForm()
      filterContext.setResetFilters(false)
    }
  }, [filterContext.resetFilters])

  const handleReset = (e) => {
    filterContext.setResetFilters(true)
    e.preventDefault()
  }

  const resetForm = () => {
    methods.reset(defaultFormValues)
    triggerClearFilters(!clearFilters)
    const storedFilters = getLocalStorage(selectedFilterStorageKey)
    const tempFilterValues = { ...storedFilters }
    for (const key in tempFilterValues[type]) {
      const tempValue =
        key !== 'exercisesUserGenerated' && key !== 'trainingsUserGenerated' && key !== 'programsUserGenerated'
          ? ''
          : false
      tempFilterValues[type][key] = tempValue
    }
    filterContext.setFrom(8)
    filterContext.setSelectedFilters(tempFilterValues)
    setLocalStorage(selectedFilterStorageKey, tempFilterValues)
  }

  const triggerFilters = () => {
    const selectedValues = methods.getValues()
    const storedFilters = getLocalStorage(selectedFilterStorageKey)
    const tempFilterValues = { ...storedFilters }
    for (const filterType in storedFilters) {
      if (filterType === type) {
        if (tempFilterValues[type]) tempFilterValues[type] = {}
        Object.keys(selectedValues).forEach((key) => {
          if (!selectedValues[key] || selectedValues[key] === 'empty') return
          tempFilterValues[filterType][key] = selectedValues[key]
        })
      }
    }
    if (tempFilterValues === storedFilters) return
    filterContext.setSelectedFilters(tempFilterValues)
  }

  const containsFilterValues = (obj) => {
    for (const key in obj) {
      if (obj[key] !== null && obj[key] !== '' && obj[key] !== false) return true
    }
    return false
  }

  return (
    <div className={`OverviewFilter${toggleState ? ` ${openClass}` : ''}`}>
      <div className="OverviewFilter-inner">
        <div className="OverviewFilter-container">
          <button className="OverviewFilter-toggle" onClick={() => setToggleState(!toggleState)}>
            <span className="OverviewFilter-toggleLabel">{t('OVERVIEWFILTER__TOGGLE_MOBILE')}</span>
            <Svg
              modifiers={['OverviewFilter-toggleIcon']}
              id={`${toggleState ? 'icon-chevron-up' : 'icon-chevron-down'}`}
            />
          </button>
          <div className="OverviewFilter-content">
            <FormProvider {...methods}>
              <form className="OverviewFilter-form" ref={formRef}>
                {data.map((filter) => {
                  return (
                    <FilterDropdown
                      clear={clearFilters}
                      defaultValue={filterContext.selectedFilters[type]?.[filter.name] || ''}
                      triggerFilters={triggerFilters}
                      filter={filter}
                      key={`FilterDropdown--${filter.id}_${type}`}
                    />
                  )
                })}
                {userContext.loggedIn && (
                  <FilterCheckbox
                    clear={clearFilters}
                    defaultValue={filterContext.selectedFilters[type]?.[`${type}UserGenerated`] === true || false}
                    triggerFilters={triggerFilters}
                    filter={{
                      id: `${type}UserGeneratedFilter`,
                      name: `${type}UserGenerated`,
                      title: t('OVERVIEWFILTER__SELFMADE')
                    }}
                    key={`FilterCheckbox_${type}`}
                  />
                )}
                {containsFilterValues(filterContext.selectedFilters[type]) && (
                  <button className="OverviewFilter-reset" onClick={handleReset}>
                    <div className="OverviewFilter-resetIcon">
                      <Svg id="icon-reset" />
                    </div>
                    <span className="OverviewFilter-resetLabel">{t('OVERVIEWFILTER__BTN_RESET')}</span>
                  </button>
                )}
              </form>
            </FormProvider>
          </div>
        </div>
      </div>
    </div>
  )
}

OverviewFilter.propTypes = {
  type: PropTypes.string,
  data: PropTypes.array
}

export default OverviewFilter
